import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useInView } from 'react-intersection-observer';
import CountUp from 'react-countup';
import { Mail, Phone, MapPinned } from 'lucide-react';

import {
  AboutSection,
  AboutContainer,
  AboutText,
  AboutTitle,
  AboutDescription,
  MetricsGrid,
  MetricCard,
  Section,
  SectionTitle,
  SectionSubtitle,
  Footer,
  FooterContent,
  FooterColumn,
  Copyright
} from '../styles';

function mulberry32(a) {
  return function () {
    a |= 0; a = (a + 0x6d2b79f5) | 0;
    let t = a ^ (a >>> 15);
    t = (t * 0x1f873d ^ t) | 0;
    return ((t ^ (t >>> 7)) >>> 0) / 2147483647;
  };
}

const DELIVERIES_SEED = 0xabcdef12;
const ONTIME_SEED     = 0xdeadbeef;

function calculateLiveMetrics() {
  const rng = mulberry32(DELIVERIES_SEED);
  const today = new Date(); today.setUTCHours(0,0,0,0);
  const now = new Date();
  const currentHour = now.getUTCHours();
  const currentSlotStart = new Date(now);
  currentSlotStart.setUTCHours(Math.floor(currentHour/2)*2,0,0,0);

  let deliveries = 1500, clients = 100, dailyClients = 0;
  const cur = new Date(today);

  while (cur < currentSlotStart) {
    const isWeekend = cur.getDay() === 0 || cur.getDay() === 6;
    if (!isWeekend) {
      const slotAdd = 7 + Math.floor(rng()*12);
      deliveries += slotAdd;
      if (dailyClients < 1 && rng() > 0.7) { clients++; dailyClients++; }
    }
    cur.setUTCHours(cur.getUTCHours()+2);
  }
  return { deliveries, clients };
}

function useLiveMetrics() {
  const [metrics, setMetrics] = useState(calculateLiveMetrics);

  useEffect(() => {
    const tick = () => {
      const today = new Date();
      if (today.getDay() === 0 || today.getDay() === 6) return;
      setMetrics(calculateLiveMetrics);
    };
    const id = setInterval(tick, 2 * 60 * 60 * 1000);
    return () => clearInterval(id);
  }, []);

  return metrics;
}

const LiveCounter = ({ label, end, suffix = '', decimals = 0 }) => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.3 });
  return (
    <MetricCard whileHover={{ scale: 1.05 }}>
      <h3 ref={ref}>
        {inView ? <CountUp end={end} duration={2.5} decimals={decimals} suffix={suffix} /> : '0'}
      </h3>
      <p>{label}</p>
    </MetricCard>
  );
};



export default function AboutPage() {
  const { deliveries, clients } = useLiveMetrics();

  const onTimeRng = mulberry32(ONTIME_SEED);
  const onTimeRate = Math.min(98, Math.max(95, 95 + Math.floor(onTimeRng() * 4)));

  const [expandedArea, setExpandedArea] = useState(null);

  return (
    <main className="usl-about-page" style={{ paddingTop: '125px' }}>

      <style>{`
        /* USL ABOUT PORTSMOUTH HERO */

        .usl-about-hero {
          display: grid;
          grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
          align-items: stretch;
          min-height: 80vh;
          padding: 32px 0 0 5%;
          box-sizing: border-box;
          background: #10243D;
          color: #ffffff;
          overflow: hidden;
        }

        .usl-about-hero-content {
          width: 100%;
          min-width: 0;
          max-width: 720px;
          margin: 0;
          padding: 0 2rem 3rem 0;
          box-sizing: border-box;
          display: flex;
          align-items: center;
        }

        .usl-about-hero-copy {
          width: 100%;
          max-width: 680px;
        
          text-align: center;
}

        .usl-about-hero h1 {
          color: #ffffff;
          font-size: clamp(2.4rem, 4vw, 3rem);
          font-weight: 800;
          line-height: 1.12;
          margin: 0 0 1.5rem;
        }

        .usl-about-hero p {
          color: rgba(255,255,255,0.9);
          font-size: clamp(1rem, 1.5vw, 1.2rem);
          font-weight: 500;
          line-height: 1.7;
          margin: 0;
        }

        .usl-about-hero-image {
          display: block;
          width: 100%;
          height: auto;
          max-height: 75vh;
          min-width: 0;
          object-fit: contain;
          object-position: right bottom;
          align-self: end;
          justify-self: end;
        
          transform: scale(1.25);
          transform-origin: right bottom;
}

        @media (max-width: 768px) {
          .usl-about-page {
            padding-top: 0 !important;
          }

          .usl-about-hero {
            grid-template-columns: minmax(0, 1fr);
            min-height: 0;
            padding: 2rem 0 0 5%;
          }

          .usl-about-hero-content {
            align-items: center;
            padding: 0 5% 2rem 0;
            max-width: 100%;
          }

          .usl-about-hero h1 {
            font-size: clamp(2rem, 8vw, 3rem);
          }

          .usl-about-hero p {
            font-size: 1rem;
            line-height: 1.7;
          }

          .usl-about-hero-image {
            transform: none;
            width: 100%;
            height: auto;
            max-height: none;
            object-fit: contain;
            object-position: right bottom;
          }
        }

        @media (max-width: 390px) {
          .usl-about-hero h1 {
            font-size: 2rem;
          }
        }

        /* Left-align only the About hero text */
        .usl-about-hero .usl-about-hero-copy,
        .usl-about-hero .usl-about-hero-copy h1,
        .usl-about-hero .usl-about-hero-copy p {
          text-align: left !important;
        }

        /* USL ABOUT OUR STORY SECTION */
        .usl-about-story {
          background: #ffffff;
          padding: 6rem 5%;
          box-sizing: border-box;
        }

        .usl-about-story-inner {
          max-width: 1200px;
          margin: 0 auto;
          display: grid;
          grid-template-columns: minmax(0, 0.9fr) minmax(0, 1.1fr);
          gap: 5rem;
          align-items: start;
        }

        .usl-about-story-heading h2 {
          color: #10243D;
          font-size: clamp(2.2rem, 4vw, 3.5rem);
          font-weight: 800;
          line-height: 1.15;
          margin: 0 0 1.5rem;
        }

        .usl-about-story-lead {
          border-left: 3px solid #00A8E8;
          padding-left: 1.5rem;
          color: #10243D;
          font-size: clamp(1.2rem, 2vw, 1.6rem);
          font-weight: 600;
          line-height: 1.55;
          margin: 0;
        }

        .usl-about-story-body p {
          color: #334155;
          font-size: 1.05rem;
          line-height: 1.85;
          margin: 0 0 1.75rem;
        }

        .usl-about-story-body p:last-child {
          margin-bottom: 0;
        }

        @media (max-width: 768px) {
          .usl-about-story {
            padding: 3.5rem 5%;
          }

          .usl-about-story-inner {
            grid-template-columns: minmax(0, 1fr);
            gap: 2rem;
          }

          .usl-about-story-lead {
            font-size: 1.2rem;
          }

          .usl-about-story-body p {
            font-size: 1rem;
            line-height: 1.75;
          }
        }
      `}</style>

      <section className="usl-about-hero" aria-labelledby="usl-about-hero-title">
        <div className="usl-about-hero-content">
          <div className="usl-about-hero-copy">
            <h1 id="usl-about-hero-title">
              Moving What Matters, Wherever It Needs to Go.
            </h1>

            <p>
              At Urgent Logistics, we believe every delivery matters.
              From urgent business deliveries and furniture transport
              to house moves and removals, we're committed to providing
              a reliable, professional and flexible service tailored
              to your needs. With dedicated teams across the South
              of England and nationwide coverage, we're here to make
              moving goods simple.
            </p>
          </div>
        </div>

        <img
          className="usl-about-hero-image"
          src="/portsmouth.webp"
          alt="Urgent Logistics in Portsmouth"
        />
      </section>

      
      {/* Original Urgent Logistics About story */}
      <section
        className="usl-about-story"
        aria-labelledby="usl-about-story-title"
      >
        <div className="usl-about-story-inner">

          <div className="usl-about-story-heading">
            <h2 id="usl-about-story-title">
              Our Story
            </h2>

            <p className="usl-about-story-lead">
              Based in Portsmouth, connected across the South,
              and delivering throughout the UK.
            </p>
          </div>

          <div className="usl-about-story-body">
            <p>
              Urgent Logistics is a UK delivery, transport and
              removals business with its main office in Portsmouth.
              With teams based in London and across Hampshire
              and Surrey, we support individuals and businesses
              who need a dependable way to get things moving.
            </p>

            <p>
              From urgent business deliveries and furniture
              transport to house moves and larger removals,
              our services are built around different needs,
              locations and schedules. Whether something needs
              to travel locally or across the country, we aim
              to make the process straightforward.
            </p>

            <p>
              We understand that moving goods is about more than
              getting from one address to another. Customers
              need clear communication, practical solutions
              and confidence that their belongings or deliveries
              are being handled with care.
            </p>

            <p>
              That's why we focus on flexible service,
              straightforward quotes and helping customers
              choose the right transport solution for the job.
              From the first enquiry through to delivery,
              our aim is to keep things simple and reliable.
            </p>
          </div>

        </div>
      </section>

      <AboutSection id="about">
              <AboutContainer>
                <AboutText initial={{opacity:0,x:-50}} whileInView={{opacity:1,x:0}} viewport={{once:true}} transition={{duration:0.8}}>
                  <AboutTitle>YOUR TRUSTED UK-WIDE DELIVERY PARTNER</AboutTitle>
                  <AboutDescription>
                    From our Portsmouth base to destinations
                    throughout the UK, Urgent Logistics offers
                    courier, transport and removals solutions
                    for individuals and businesses.
                    Our services include same-day deliveries,
                    overnight transport, multi-drop routes
                    and UK-wide moves — with flexibility
                    and clear communication at the heart
                    of what we do.
                  </AboutDescription>
                </AboutText>

                <MetricsGrid initial={{opacity:0,x:50}} whileInView={{opacity:1,x:0}} viewport={{once:true}} transition={{duration:0.8}}>
                  <LiveCounter label="Deliveries Available" end={24} suffix="/7"/>
                  <LiveCounter label="Happy Clients" end={clients} suffix="+"/>
                  <LiveCounter label="Deliveries Made" end={deliveries} suffix="+"/>
                  <LiveCounter label="On-Time Rate" end={onTimeRate} suffix="%"/>
                </MetricsGrid>
              </AboutContainer>
            </AboutSection>

      <Section id="areas-we-serve" style={{ background: '#F8F7F4' }}>
        <style>{`
          @media (max-width: 768px) {
            .area-pill.hidden-mobile { display: none; }
            .area-row { flex-direction: column !important; align-items: center !important; text-align: center; }
            .area-img { margin: 0 auto 1rem auto !important; width: 100% !important; max-width: 300px !important; height: auto !important; aspect-ratio: 1/1; }
            .area-content { text-align: center; }
            .area-content .see-more-btn { margin: 0.5rem auto 0 auto !important; display: block; }
          }
          @media (min-width: 769px) {
            .see-more-btn { display: none !important; }
          }
        `}</style>
        <SectionTitle
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          style={{ color: '#1a1a1a', marginBottom: '0.5rem' }}
        >
          Areas We Serve
        </SectionTitle>
        <SectionSubtitle
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          style={{ color: '#1a1a1a', marginBottom: '2rem' }}
        >
          Covering every town and village across Hampshire, Guildford &amp; Surrey, and London. If you don&apos;t see your area, give us a message anyway.
        </SectionSubtitle>

        <div style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          gap: '2rem', 
          maxWidth: '1200px', 
          margin: '0 auto' 
        }}>
          {[
            { 
              name: 'Hampshire', 
              img: '/customer4.webp',
              text: 'Covering every town and village across the entire county, including:',
              towns: ['Portsmouth', 'Southampton', 'Basingstoke', 'Andover', 'Winchester', 'Farnborough', 'Aldershot', 'Eastleigh', 'Havant', 'Fareham', 'Gosport', 'Waterlooville', 'Fleet', 'Petersfield', 'Romsey', 'Lymington', 'Bishop\'s Waltham', 'Totton'] 
            },
            { 
              name: 'Guildford & Surrey', 
              img: '/customer5.webp',
              text: 'Covering every town and village in and around Surrey, including:',
              towns: ['Guildford', 'Woking', 'Godalming', 'Farnham', 'Haslemere', 'Cranleigh', 'Epsom', 'Camberley', 'Staines', 'Weybridge', 'Leatherhead', 'Dorking', 'Reigate', 'Redhill', 'Oxted', 'Caterham'] 
            },
            { 
              name: 'London', 
              img: '/customer3.webp',
              text: 'Covering every borough and area across Greater London, including:',
              towns: ['Central London', 'Croydon', 'Kingston upon Thames', 'Richmond', 'Bromley', 'Ealing', 'Wimbledon', 'Ilford', 'Romford', 'Enfield', 'Barnet', 'Harrow', 'Uxbridge', 'Hounslow', 'Sutton', 'Wembley'] 
            }
          ].map((area, index) => (
            <div key={area.name} style={{ padding: '2.5rem 0', borderBottom: index !== 2 ? '1px solid #10243D' : 'none' }}>
              
              {/* Flex row to keep image on left and content on right */}
              <div className="area-row" style={{ display: 'flex', flexDirection: 'row', gap: '2rem', alignItems: 'flex-start' }}>
                
                {/* Square Image on the left */}
                <img 
                  className="area-img"
                  src={area.img} 
                  alt={area.name} 
                  style={{ 
                    width: '180px', 
                    height: '180px', 
                    objectFit: 'cover', 
                    borderRadius: '16px', 
                    flexShrink: 0 
                  }} 
                />

                {/* Content on the right */}
                <div className="area-content" style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ marginBottom: '1.5rem' }}>
                    <h3 style={{ fontSize: '1.5rem', color: '#10243D', margin: 0, marginBottom: '0.4rem', fontWeight: '700', lineHeight: '1.2' }}>{area.name}</h3>
                    <p style={{ color: '#10243D', margin: 0, lineHeight: '1.6', fontWeight: '500' }}>
                      {area.text}
                    </p>
                  </div>
                  
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'center', width: '100%' }}>
                    {area.towns.map((city, i) => {
                      const isHidden = i >= 11 && expandedArea !== area.name;
                      return (
                        <span 
                          key={city} 
                          className={isHidden ? 'area-pill hidden-mobile' : 'area-pill'} 
                          style={{ background: '#fff', border: '1px solid #cbd5e1', color: '#10243D', padding: '0.4rem 0.8rem', borderRadius: '50px', fontSize: '0.85rem', fontWeight: '600', whiteSpace: 'nowrap', flexShrink: 0 }}
                        >
                          {city}
                        </span>
                      );
                    })}
                    
                    <button 
                      type="button"
                      onClick={() => setExpandedArea(expandedArea === area.name ? null : area.name)}
                      className="see-more-btn"
                      style={{ 
                        background: 'transparent', 
                        border: 'none', 
                        color: '#00A8E8', 
                        fontWeight: '700', 
                        cursor: 'pointer', 
                        fontSize: '0.9rem',
                        display: 'flex',
                        alignItems: 'center',
                        padding: '0.4rem 0.5rem',
                        marginLeft: 'auto',
                        whiteSpace: 'nowrap',
                        flexShrink: 0
                      }}
                    >
                      {expandedArea === area.name ? 'See less' : 'See more...'}
                    </button>
                  </div>
                </div>
              </div>
              
            </div>
          ))}
        </div>
      </Section>

      <section
        style={{
          background: '#10243D',
          padding: '65px 5%',
          textAlign: 'center'
        }}
      >
        <h2
          style={{
            color: '#FFFFFF',
            fontSize: 'clamp(1.7rem, 3vw, 2.3rem)',
            margin: '0 0 15px'
          }}
        >
          Need Something Delivered or Moved?
        </h2>

        <p
          style={{
            color: '#D8E5F0',
            maxWidth: '650px',
            margin: '0 auto 25px',
            lineHeight: 1.7
          }}
        >
          Wherever you are in the UK, tell us about your
          delivery or moving requirements and we'll be
          happy to help.
        </p>

        <Link
          to="/get-a-quote"
          style={{
            display: 'inline-block',
            background: '#00A8E8',
            color: '#FFFFFF',
            padding: '15px 28px',
            borderRadius: '8px',
            fontWeight: 700,
            textDecoration: 'none'
          }}
        >
          Get a Free Quote
        </Link>
      </section>

      <Footer>
              <FooterContent className="responsive-footer">
                <FooterColumn>
                  <h4>Urgent Logistics</h4>
                  <p>Reliable courier, transport and removals services across the UK, including London and the South Coast. From business deliveries to house moves and furniture transport, Urgent Logistics offers flexible solutions to get your items moving safely and efficiently.</p>
                </FooterColumn>
                <FooterColumn>
                  <h4>Contact Info</h4>
                  <p style={{display:'flex',alignItems:'center',gap:'8px',margin:'0 0 12px'}}><Mail size={16}/> info@urgentservicelogistics.co.uk</p>
                  <p style={{display:'flex',alignItems:'center',gap:'8px',margin:'0 0 12px'}}><Phone size={16}/><a href="tel:+447436117842" style={{color:'inherit',textDecoration:'none',lineHeight:'inherit'}}>07436 117842</a></p>
                  <p style={{display:'flex',alignItems:'center',gap:'8px',margin:'0 0 12px'}}><MapPinned size={16}/> Hampshire</p>
                </FooterColumn>
                <FooterColumn>
                  <h4>Operating Hours</h4>
                  <p style={{margin:'0 0 8px', lineHeight:'1.6'}}><strong>Deliveries:</strong> 24 Hours, 7 Days</p>
                  <p style={{margin:'0 0 8px', lineHeight:'1.6'}}><strong>Phone Support:</strong></p>
                  <p style={{margin:'0 0 8px', lineHeight:'1.6'}}>Mon &ndash; Sat: 08:00 am &ndash; 20:00 pm</p>
                  <p style={{margin:'0 0 8px', lineHeight:'1.6'}}>Sun: 09:00 am &ndash; 21:00 pm</p>
                </FooterColumn>
              </FooterContent>
              <Copyright>
                © 2025 Urgent Logistics. All rights reserved. 
                Website by <a href="https://draftpoint.co.uk" target="_blank" rel="noopener noreferrer" style={{color:'#ff6b35',textDecoration:'none'}}>Draftpoint</a>
              </Copyright>
            </Footer>

    </main>
  );
}
